/**
 * `/app/projects` — `mockups/client/projects.html`.
 *
 * Fixed-scope work, in three ranks: the projects under way, each with its own
 * region and its schedule; the quotes waiting on a decision, as the compact
 * `DecisionCard` the requests page owns in full; and the delivered ones. The one
 * hero is **how many milestones have been delivered** on the first live project —
 * session 41 (C1), where it was still to invoice (`.docs/19:63`) until the page was
 * asked to open on progress rather than on money.
 *
 * **What this page reads, and why it is five calls.** `GET
 * /api/clients/me/projects` answers the work order and its milestones and nothing
 * else, so four of the mockup's facts come from elsewhere:
 *
 *   - the **invoice** a milestone produced — `InvoiceSchema` carries
 *     `work_order_ref` and `milestone_seq` for exactly this ("The project or extra
 *     this invoice is for"), and `MilestoneView.invoice_id` is a UUID a client is
 *     never shown;
 *   - the **decision** on a proposed quote, which `client-decisions` owns;
 *   - the **template's name** — the mockup's `<span class="tag">Business site, up to
 *     6 pages</span>`. `work_order.template` is a code, and `GET /api/catalog`'s
 *     `projects[].sizes[].name` is where the words are;
 *   - the **scope changes** on a project, which `LiveProject` reads off `GET
 *     /api/clients/me/projects/:ref` — one call per live project, made in the region
 *     that needs it.
 *
 * `useRequests('all')` is the fallback for the last of those and nothing else: the
 * ledger is cursor-paginated, so a project whose scope change is older than the first
 * page's oldest row would silently lose it out of the hero. The project's own detail
 * is the authority; the ledger is what the region draws until it lands.
 *
 * All of them are joins a read model should be doing (`.docs/00-index.md` §5:
 * "Cross-module read models for a page → `apps/api/src/readmodels/`"). Reported to
 * the lead as a gap in the projects read model rather than solved twice here.
 *
 * **The `Active | All` seg is the URL.** `?show=all` adds the delivered and the
 * closed projects; `?show=active` is the default. `ARCHITECTURE.md` §Frontend —
 * the URL is the state, and the back button has to work.
 *
 * **A `billing` seat is refused the page, inline.** `.docs/19` §Edge cases lists
 * projects among the three, so the nav item is not rendered and a typed URL gets the
 * one-line `RoleRefusal` rather than a blank screen — and the refusal is decided
 * before the five reads, so the page a role may not have also costs nothing to
 * refuse.
 */

import { Link } from '@tanstack/react-router'
import {
  dateOnlyOf,
  type ClientDecisionItemView,
  type InvoiceView,
  type WorkOrderView,
} from '@fareast/shared'
import { z } from 'zod'

import { CompactDecision } from '../../../components/client/DecisionCard.js'
import { Button, Seg, SegItem } from '../../../components/portal/Button.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Section, SectionHead, Stack } from '../../../components/portal/Layout.js'
import { DayText, More, Muted, Prose, Ref, Small } from '../../../components/portal/Text.js'
import {
  useCatalog,
  useDecisions,
  useInvoices,
  useProjects,
  useRequests,
} from '../../../api/client/index.js'
import { usePrincipal } from '../../../auth/principal.js'
import { useFormat } from '../../../format/useFormat.js'
import { now } from '../../../integrations/clock.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { RoleRefusal } from '../RoleRefusal.js'
import { useRequestComposer } from '../requests/RequestComposer.js'
import { abilitiesOf } from '../settings/roles.js'
import { DeliveredProject } from './DeliveredProject.js'
import { LiveProject } from './LiveProject.js'
import {
  isAwaiting,
  isClosed,
  isFinished,
  isLive,
  scopeChangesOf,
  templateNameOf,
  type ProjectRow,
} from './facts.js'

/** Which projects the page is showing. `active` is the default. */
export const ProjectsSearchSchema = z.object({
  show: z.enum(['active', 'all']).optional(),
})

export type ProjectsFilter = 'active' | 'all'

export function ProjectsPage() {
  /*
   * Before the five reads and before the seg: a role that may not see the page may
   * not see it in any of its states, and a wrapper is the only place a hook rule
   * lets that be true.
   */
  if (!abilitiesOf(usePrincipal()).raisesRequests) {
    return (
      <>
        <PageTitle eyebrow="Work orders" title="Projects" />
        <RoleRefusal what="projects" />
      </>
    )
  }

  return <Projects />
}

function Projects() {
  const [search, setSearch] = useSearchState(ProjectsSearchSchema)
  const filter: ProjectsFilter = search.show ?? 'active'

  const projects = useProjects()
  const decisions = useDecisions()
  const invoices = useInvoices()
  const catalog = useCatalog()
  /*
   * The whole ledger, not the ninety-day window the requests page defaults to: a
   * scope change is billed on its project's final milestone, and a project can
   * easily outlive that window. It is still only the **fallback** for the scope
   * changes — see the header — because this read is a cursor page and a project's
   * own detail is not.
   */
  const requests = useRequests('all')
  const composer = useRequestComposer()

  const format = useFormat()
  const today = dateOnlyOf(now(), format.zone)

  const title = (
    <PageTitle
      eyebrow="Work orders"
      title="Projects"
      actions={
        <>
          <Seg label="Which projects">
            <SegItem
              on={filter === 'active'}
              onClick={() => {
                setSearch({ show: 'active' })
              }}
            >
              Active
            </SegItem>
            <SegItem
              on={filter === 'all'}
              onClick={() => {
                setSearch({ show: 'all' })
              }}
            >
              All
            </SegItem>
          </Seg>
          {/*
            The composer itself, over this page. It was a link to the card on
            Requests — with `#new` since session 41 (C2), because without the hash it
            landed 1590px above the form it promises. The window is the form, here.
          */}
          {composer === null ? null : (
            <Button variant="primary" onClick={composer.open}>
              Start a project
            </Button>
          )}
        </>
      }
    />
  )

  /*
   * One muted line while any of the five are in flight, and no skeleton
   * (`.docs/19` §Design decisions). The page is one screen with one answer on it;
   * showing three quarters of it and moving the hero when the fifth lands is
   * worse than waiting.
   */
  if (
    projects.isPending ||
    decisions.isPending ||
    invoices.isPending ||
    catalog.isPending ||
    requests.isPending ||
    projects.data === undefined
  ) {
    return (
      <>
        {title}
        <Empty>Loading your projects&hellip;</Empty>
      </>
    )
  }

  const rows: readonly ProjectRow[] = projects.data.projects
  const invoiceRows: readonly InvoiceView[] = invoices.data?.items ?? []
  const requestRows: readonly WorkOrderView[] = requests.data?.requests ?? []
  const openQuotes: readonly ClientDecisionItemView[] =
    decisions.data?.items.filter((one) => one.kind === 'quote') ?? []

  if (rows.length === 0) {
    return (
      <>
        {title}
        <Empty>No projects yet.</Empty>
      </>
    )
  }

  const live = rows.filter(isLive)
  const awaiting = rows.filter(isAwaiting)
  const finished = rows.filter(isFinished)
  const closed = rows.filter(isClosed)
  /* What `Active` is hiding, and therefore what the "N finished or closed →" line counts. */
  const behindTheSeg = filter === 'active' ? finished.length + closed.length : 0
  /*
   * What is actually on screen. Not `behindTheSeg === 0`, which is zero for **every**
   * `?show=all` — so a client with nothing but delivered work got "Nothing under way."
   * printed above a populated Delivered section.
   */
  const onScreen =
    live.length + awaiting.length + (filter === 'all' ? finished.length + closed.length : 0)

  return (
    <>
      {title}

      {live.map((project, index) => (
        <LiveProject
          key={project.work_order.ref}
          project={project}
          invoices={invoiceRows}
          scopeChanges={scopeChangesOf(requestRows, project)}
          templateName={templateNameOf(catalog.data, project.work_order.template)}
          today={today}
          lead={index === 0}
        />
      ))}

      {awaiting.length === 0 ? null : (
        <Section>
          <SectionHead title="Awaiting approval" />
          <AwaitingProjects projects={awaiting} decisions={openQuotes} />
        </Section>
      )}

      {filter === 'all' && finished.length > 0 ? (
        <Section>
          <SectionHead title="Delivered" />
          <Stack>
            {finished.map((project) => (
              <DeliveredProject
                key={project.work_order.ref}
                project={project}
                invoices={invoiceRows}
                templateName={templateNameOf(catalog.data, project.work_order.template)}
              />
            ))}
          </Stack>
        </Section>
      ) : null}

      {filter === 'all' && closed.length > 0 ? (
        <Section>
          <SectionHead title="Not going ahead" />
          <Card>
            <CardBody>
              <Stack gap="tight">
                {closed.map((project) => (
                  <Small key={project.work_order.ref}>
                    <Muted>
                      {project.work_order.title} &middot; <Ref value={project.work_order.ref} />{' '}
                      &middot; {project.work_order.status === 'declined' ? 'declined' : 'cancelled'}
                    </Muted>
                  </Small>
                ))}
              </Stack>
            </CardBody>
          </Card>
        </Section>
      ) : null}

      {onScreen === 0 ? <Empty>Nothing under way.</Empty> : null}

      {/* DESIGN.md P3 — show the exception, and summarise the rest in one line. */}
      {behindTheSeg === 0 ? null : (
        <Prose>
          <More asChild>
            <Link to="/app/projects" search={{ show: 'all' }}>
              {behindTheSeg === 1
                ? 'One project that is finished or closed'
                : `${String(behindTheSeg)} projects that are finished or closed`}{' '}
              &rarr;
            </Link>
          </More>
        </Prose>
      )}
    </>
  )
}

/* ------------------------------------------------------------------- awaiting */

/**
 * The quotes waiting on the client.
 *
 * A quote that is still answerable is a **decision**, and `CompactDecision` is the
 * only rendering of one (`.docs/19` §DRY watchlist) — a second approve button here
 * would be a second confirm dialog and a second place to get the idempotency key
 * wrong, so it links to the card on the requests page.
 *
 * A quote whose validity has run out is **not** a decision:
 * `work_orders.openDecisionsForClient` leaves it out on purpose, so it has no row
 * in `client-decisions`. It must not be ochre either — ochre is "this needs your
 * decision, or this costs you more", and there is nothing the reader can do with
 * an expired quote but ask for a new one.
 */
function AwaitingProjects({
  projects,
  decisions,
}: {
  readonly projects: readonly ProjectRow[]
  readonly decisions: readonly ClientDecisionItemView[]
}) {
  const open = projects
    .map((project) => ({
      project,
      decision: decisions.find((one) => one.ref === project.work_order.ref),
    }))
    .filter(
      (pair): pair is { project: ProjectRow; decision: ClientDecisionItemView } =>
        pair.decision !== undefined,
    )
  const expired = projects.filter(
    (project) => !decisions.some((one) => one.ref === project.work_order.ref),
  )

  return (
    <Stack>
      {open.length === 0 ? null : (
        <Card need>
          <CardBody tight>
            {open.map(({ project, decision }) => (
              <CompactDecision key={project.work_order.ref} decision={decision} showAmount />
            ))}
          </CardBody>
        </Card>
      )}

      {expired.length === 0 ? null : (
        <Card>
          <CardBody>
            <Stack gap="tight">
              {expired.map((project) => (
                <Small key={project.work_order.ref}>
                  <Muted>
                    {project.work_order.title} &middot; <Ref value={project.work_order.ref} />
                    {project.work_order.quote_valid_until === null ? null : (
                      <>
                        {' '}
                        &middot; the quote ran out on{' '}
                        <DayText value={project.work_order.quote_valid_until} />
                      </>
                    )}
                    . Ask us to re-quote it.
                  </Muted>
                </Small>
              ))}
            </Stack>
          </CardBody>
        </Card>
      )}
    </Stack>
  )
}
